'use client'

import * as React from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { IconBtn, pillBtnBase } from '@/components/roostr'
import { UpgradePrompt } from '@/components/subscription'
import { useSubscription } from '@/lib/subscription'
import { api } from '@/lib/api'
import { useSettingsStore } from '@/lib/store'
import { useI18n } from '@/lib/i18n'
import type { Listing } from '@/lib/types'
import { AiSummaryPanel } from './ai-summary'

export type AiSummaryButtonVariant = 'pill' | 'icon'

const GAP = 8 // gap between the trigger and the popover
const MARGIN = 8 // min distance the popover keeps from the viewport edges

interface PopCoords {
  top?: number
  bottom?: number
  left: number
  width: number
  maxHeight: number
}

// AI-summary trigger + popover, shared by the review card (notifications.jsx:35-58
// — a labeled "Summary" pill) and the grid listing card (cards.jsx:304-322 — a
// 28px auto_awesome icon next to the title). Production swaps the prototype's
// canned text for the real premium summarize flow: the 'just_the_facts' gate +
// api.summarizeDescription, same as the detail page. Render only when the
// listing has a description to summarize.
//
// The popover is PORTALED to <body> with fixed, viewport-clamped positioning:
// the listing card clips itself with `overflow: hidden` (to round its image),
// which would otherwise crop a long summary at the card edge. Portaling escapes
// that clip and lets the popover cap its height + scroll near the screen edges.
export function AiSummaryButton({
  listing,
  variant = 'pill',
}: {
  listing: Listing
  variant?: AiSummaryButtonVariant
}) {
  const { t } = useI18n()
  const { canUse } = useSubscription()
  const [open, setOpen] = React.useState(false)
  const [showUpgrade, setShowUpgrade] = React.useState(false)
  const [summaryText, setSummaryText] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(false)
  const [failed, setFailed] = React.useState(false)
  const [coords, setCoords] = React.useState<PopCoords | null>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const popRef = React.useRef<HTMLDivElement>(null)

  const isIcon = variant === 'icon'
  const popWidth = isIcon ? 300 : 340

  // Position the portaled popover from the trigger's viewport rect: right edge
  // aligned to the trigger, clamped to the viewport horizontally. Placed below
  // the trigger by default, but flipped above when there is little room below
  // and clearly more above; either way the height is capped to the room on that
  // side so the body scrolls instead of running off-screen.
  const reposition = React.useCallback(() => {
    const btn = triggerRef.current
    if (!btn) return
    const r = btn.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const width = Math.min(popWidth, vw - MARGIN * 2)
    const left = Math.max(MARGIN, Math.min(r.right - width, vw - MARGIN - width))
    const roomBelow = vh - r.bottom - GAP - MARGIN
    const roomAbove = r.top - GAP - MARGIN
    if (roomBelow >= 220 || roomBelow >= roomAbove) {
      setCoords({ top: r.bottom + GAP, left, width, maxHeight: Math.max(120, roomBelow) })
    } else {
      setCoords({ bottom: vh - r.top + GAP, left, width, maxHeight: Math.max(120, roomAbove) })
    }
  }, [popWidth])

  // While open: position now, then keep in sync with scroll (capture, so nested
  // scroll containers count) and resize.
  React.useEffect(() => {
    if (!open) return
    reposition()
    const onMove = () => reposition()
    window.addEventListener('scroll', onMove, true)
    window.addEventListener('resize', onMove)
    return () => {
      window.removeEventListener('scroll', onMove, true)
      window.removeEventListener('resize', onMove)
    }
  }, [open, reposition])

  // Outside-mousedown closes — check both the trigger and the portaled popover
  // (design notifications.jsx:11-16 / cards.jsx:272-277).
  React.useEffect(() => {
    if (!open) return
    const h = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (popRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [open])

  const summarize = React.useCallback(async () => {
    const text = listing.description
    if (!text) return
    setLoading(true)
    setFailed(false)
    try {
      // AI summary language — the user's setting (Settings → Language).
      const res = await api.summarizeDescription(text, useSettingsStore.getState().aiSummaryLanguage)
      setSummaryText(res.summary)
    } catch (err) {
      console.error('Summarization failed:', err)
      setFailed(true)
    } finally {
      setLoading(false)
    }
  }, [listing.description])

  // stopPropagation so the icon variant, which sits inside the clickable card,
  // doesn't open the listing when the button is tapped (design cards.jsx:307).
  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (open) {
      setOpen(false)
      return
    }
    if (!canUse('just_the_facts')) {
      setShowUpgrade(true)
      return
    }
    setOpen(true)
    if (!summaryText && !loading) void summarize()
  }

  const tip = t('notifications.summaryTip', 'AI summary of the listing description')

  const popover =
    open && coords && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={popRef}
            style={{
              position: 'fixed',
              top: coords.top,
              bottom: coords.bottom,
              left: coords.left,
              width: coords.width,
              maxHeight: coords.maxHeight,
            }}
            className="z-[60] animate-pop-in overflow-y-auto overscroll-contain rounded-shape-md border border-outline bg-surface p-1 shadow-e3"
          >
            {loading ? (
              <div className="flex items-center gap-2 px-3 py-[10px] text-[12.5px] text-ink-secondary">
                <Loader2 className="h-[15px] w-[15px] animate-spin" />
                {t('listingDetail.summarizing', 'Summarizing...')}
              </div>
            ) : failed ? (
              <div className="px-3 py-[10px] text-[12.5px] text-ink-secondary">
                {t('notifications.summaryError', 'The summary could not be generated.')}{' '}
                <button type="button" onClick={() => void summarize()} className="font-semibold text-primary">
                  {t('notifications.tryAgain', 'Try again')}
                </button>
              </div>
            ) : summaryText ? (
              <AiSummaryPanel summaryText={summaryText} className="bg-surface" />
            ) : null}
          </div>,
          document.body
        )
      : null

  return (
    <span className={cn('shrink-0', isIcon && 'inline-flex')}>
      {isIcon ? (
        <IconBtn
          ref={triggerRef}
          size={28}
          onClick={toggle}
          title={tip}
          aria-label={tip}
          className={open ? 'bg-primary-container text-on-primary-container' : 'bg-surface-low text-ink-secondary'}
        >
          <Sparkles size={14} />
        </IconBtn>
      ) : (
        <button
          ref={triggerRef}
          type="button"
          onClick={toggle}
          title={tip}
          className={cn(
            pillBtnBase,
            'px-[11px] py-[5px] text-[12px]',
            open ? 'bg-primary-container text-on-primary-container' : 'bg-surface-low text-ink-secondary'
          )}
        >
          <Sparkles size={13} />
          {t('notifications.summary', 'Summary')}
        </button>
      )}
      {popover}
      <UpgradePrompt open={showUpgrade} onOpenChange={setShowUpgrade} feature="just_the_facts" />
    </span>
  )
}
